/**
 * Per-mode keyboard/mouse binding table for the controls overlay (issue #87).
 *
 * This is the single source of truth the overlay renders: a future binding
 * change (new key, new mode, reworded label) edits the table below only.
 * Deliberately three.js- and DOM-free so it is unit-tested in plain Node,
 * mirroring the RouteRibbon/BuildingsLayer split of pure data vs. a thin DOM
 * wrapper (`controlsOverlay.ts` owns rendering).
 *
 * Bindings verified against camera/cameraRig.ts and camera/routePlayer.ts on
 * `staging` (issue #67 settled the arrow-look keys; nothing here changes any
 * binding, it only documents the existing ones).
 */
import type { NavMode } from '../camera/cameraRig';

/**
 * One key shown as a keycap. `code` is a physical `KeyboardEvent.code`
 * (e.g. `'KeyW'`) resolved to the user's actual layout letter at render time
 * (issue #87: AZERTY etc. see their own letters); `literal` is shown verbatim
 * and is never layout-resolved (arrows, `Shift`, `−`/`+`, mouse actions).
 * Exactly one of the two is set.
 *
 * `modifier` is a held qualifier shown in front of the key, e.g. `'Shift'` for
 * Explore's Shift+E/Q altitude elevator (issue #184). It is deliberately part
 * of the token rather than a separate literal keycap so that `Shift`+E is a
 * different binding from bare E — {@link hasDuplicateKeyWithinMode} folds it
 * into the key's identity — while the base key stays a physical code and keeps
 * resolving to the user's own layout letter.
 */
export type KeyToken =
  | { code: string; literal?: undefined; modifier?: string }
  | { code?: undefined; literal: string; modifier?: string };

export interface KeycapRow {
  keys: KeyToken[];
  /** Short plain-English label shown beside the keycap(s), e.g. "move". */
  label: string;
}

function code(value: string): KeyToken {
  return { code: value };
}

function literal(value: string): KeyToken {
  return { literal: value };
}

/** A physical-code keycap held together with `Shift` (issue #184). */
function shiftCode(value: string): KeyToken {
  return { code: value, modifier: 'Shift' };
}

/** The view-scale toggle (issue #95) works identically in every camera mode
 * — it only ever touches camera.fov, never position/mode/aim — so it is
 * appended as the last row of every mode's set below rather than living in
 * just one. `KeyV` is a physical code (layout-resolved like WASD/E/Q), not a
 * literal, since it is a real keyboard letter. */
const VIEW_SCALE_ROW: KeycapRow = { keys: [code('KeyV')], label: 'view scale' };

/** Per-mode row sets (parent spec §"Behaviour" table, issue #87). Row order
 * is the display order. */
export const CONTROLS_BINDINGS: Record<NavMode, KeycapRow[]> = {
  orbit: [
    { keys: [literal('drag')], label: 'rotate' },
    { keys: [literal('scroll')], label: 'zoom' },
    { keys: [literal('right-drag')], label: 'pan (mouse/trackpad only)' },
    VIEW_SCALE_ROW,
  ],
  // Explore (issue #142): orbit's drag/zoom plus keyboard driving of the
  // focus. WASD/E/Q are physical codes (layout-resolved like the other modes);
  // the arrows and drag/scroll are literals. Rows are verified against
  // cameraRig.ts's updateExplore keys.
  explore: [
    { keys: [code('KeyW'), code('KeyA'), code('KeyS'), code('KeyD')], label: 'move focus' },
    // Plain E/Q moves the eye over a pivot pinned to the terrain, so the view
    // tilts toward plan and pulls back — it does not climb. The old "up / down"
    // label promised an altitude control this row never was (issue #184); the
    // motion itself is unchanged, only the wording is honest now.
    { keys: [code('KeyE'), code('KeyQ')], label: 'swing up / down' },
    { keys: [shiftCode('KeyE'), shiftCode('KeyQ')], label: 'altitude up / down' },
    { keys: [literal('←'), literal('→')], label: 'turn' },
    { keys: [literal('↑'), literal('↓')], label: 'tilt' },
    { keys: [literal('drag')], label: 'rotate' },
    { keys: [literal('scroll')], label: 'zoom' },
    VIEW_SCALE_ROW,
  ],
  fly: [
    { keys: [code('KeyW'), code('KeyA'), code('KeyS'), code('KeyD')], label: 'move' },
    { keys: [code('KeyE'), code('KeyQ')], label: 'up / down' },
    { keys: [literal('↑'), literal('↓'), literal('←'), literal('→')], label: 'look' },
    { keys: [literal('Shift')], label: 'hold to go faster' },
    { keys: [literal('−'), literal('+'), literal('scroll')], label: 'speed' },
    { keys: [literal('click')], label: 'mouse-look' },
    VIEW_SCALE_ROW,
  ],
  walk: [
    { keys: [code('KeyW'), code('KeyA'), code('KeyS'), code('KeyD')], label: 'walk' },
    { keys: [literal('↑'), literal('↓'), literal('←'), literal('→')], label: 'look' },
    { keys: [literal('Shift')], label: 'hold to run' },
    { keys: [literal('click')], label: 'mouse-look' },
    VIEW_SCALE_ROW,
  ],
  route: [
    { keys: [literal('↑'), literal('↓'), literal('←'), literal('→')], label: 'look around' },
    { keys: [literal('−'), literal('+')], label: 'slower / faster' },
    VIEW_SCALE_ROW,
  ],
};

/** Every mode the overlay knows rows for, in top-bar order. */
export const NAV_MODES: readonly NavMode[] = ['orbit', 'explore', 'fly', 'walk', 'route'];

/**
 * Resolves a token's displayed glyph. Literal tokens pass through unchanged.
 * Physical-key tokens look up `layoutMap` (from
 * `navigator.keyboard.getLayoutMap()`, issue #87) for the user's actual
 * layout letter; with no map (unavailable/rejected/not yet resolved) or no
 * entry for that code, falls back to the QWERTY letter implied by the code
 * itself (`'KeyW'` -> `'W'`) — never blocks or throws.
 */
export function resolveKeyLabel(token: KeyToken, layoutMap: ReadonlyMap<string, string> | null | undefined): string {
  const base = resolveBaseKeyLabel(token, layoutMap);
  return token.modifier ? `${token.modifier} + ${base}` : base;
}

/** The token's own glyph, before any modifier prefix. */
function resolveBaseKeyLabel(token: KeyToken, layoutMap: ReadonlyMap<string, string> | null | undefined): string {
  if (token.literal !== undefined) return token.literal;
  const mapped = layoutMap?.get(token.code);
  if (mapped) return mapped.toUpperCase();
  return qwertyFallback(token.code);
}

/** Best-effort QWERTY label for a `KeyboardEvent.code` with no layout-map
 * entry. Every code used in {@link CONTROLS_BINDINGS} is a `'Key*'` letter
 * code, so stripping the `'Key'` prefix recovers the QWERTY letter; any
 * future non-letter code without a map entry falls back to the raw code
 * rather than guessing. */
function qwertyFallback(keyCode: string): string {
  return keyCode.startsWith('Key') ? keyCode.slice(3) : keyCode;
}

/** True if any mode's rows repeat the same key (code or literal, and the same
 * modifier) within that mode — a data-entry bug the unit tests guard against
 * directly. The modifier is part of the identity because `Shift`+E and bare E
 * are two distinct bindings in Explore (issue #184), not a duplicate. */
export function hasDuplicateKeyWithinMode(rows: KeycapRow[]): boolean {
  const seen = new Set<string>();
  for (const row of rows) {
    for (const key of row.keys) {
      const base = key.literal !== undefined ? `literal:${key.literal}` : `code:${key.code}`;
      const id = key.modifier !== undefined ? `${key.modifier}+${base}` : base;
      if (seen.has(id)) return true;
      seen.add(id);
    }
  }
  return false;
}
